import { useCallback } from 'react';
import { TextFieldProps } from '@mui/material';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { PickerValue } from '@mui/x-date-pickers/internals';
import {
  jsDateToLocalISO8601DateString,
  getDateFromDateString,
  convertDateToTimezone,
  getLocalTimeZoneId,
} from '../../lib';

interface Props extends Omit<DatePickerProps, 'onChange' | 'value'> {
  onChange: (value: string | null, pickerValue?: PickerValue) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  name: string;
  value: any;
  required?: boolean;
  textFieldProps?: TextFieldProps;
}

export function DateInput({
  onBlur,
  onChange,
  value,
  name,
  required,
  ...rest
}: Props) {
  const { textFieldProps = {}, ...props } = rest;

  const handleBlur = useCallback(
    (e: React.FocusEvent<any>) => {
      // Create a new event with the field name explicitly set
      // https://formik.org/docs/api/formik#handleblur-e-any--void
      const eventWithName = {
        ...e,
        target: {
          ...e.target,
          name,
        },
      };
      onBlur && onBlur(eventWithName);
    },
    [onBlur, name],
  );

  const handleChange = useCallback(
    (date) => {
      // Needed because of https://github.com/mui-org/material-ui-pickers/issues/1526
      onChange(date ? jsDateToLocalISO8601DateString(date) : null, date);
    },
    [onChange],
  );

  return (
    <DatePicker
      value={toLocalDate(value) ?? null}
      format='MM/dd/yyyy'
      onChange={handleChange}
      name={name}
      slotProps={{
        textField: {
          name,
          variant: 'standard',
          onBlur: handleBlur,
          required,
          ...textFieldProps,
        },
      }}
      {...props}
    />
  );
}

function toLocalDate(s: string | Date | null) {
  if (!s) return null;
  if (s instanceof Date)
    return convertDateToTimezone({ date: s, timezone: getLocalTimeZoneId() });
  return getDateFromDateString({ dateStr: s });
}
